import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const AppContext = createContext(null);

// AED is the base currency (rate = 1). Others convert from AED.
export const CURRENCIES = [
  { code: "AED", label: "AED", symbol: "AED ", rate: 1 },
  { code: "USD", label: "USD", symbol: "$", rate: 0.272 },
  { code: "GBP", label: "GBP", symbol: "£", rate: 0.215 },
  { code: "EUR", label: "EUR", symbol: "€", rate: 0.253 },
];

export const LANGUAGES = [
  { code: "en", label: "English", short: "EN", dir: "ltr" },
  { code: "de", label: "Deutsch", short: "DE", dir: "ltr" },
  { code: "es", label: "Español", short: "ES", dir: "ltr" },
  { code: "ar", label: "العربية", short: "AR", dir: "ltr" },
];

// English is the source-of-truth dictionary; other languages fall back to EN.
const DICT = {
  en: {
    nav: {
      home: "Home",
      crm: "CRM",
      websites: "Websites",
      websites_custom: "Custom Websites",
      websites_landing: "Landing Pages",
      landing: "Landing Pages",
      work: "Our Work",
      care: "OrangeCare",
      contact: "Contact",
      cta: "Build my system",
    },
    promo: {
      launch_title: "Launch Offer:",
      launch_desc_prefix: "Professional landing page for only",
      launch_sub: "Perfect for product launches, campaigns and new offers. Limited time only.",
      was: "Was",
      now: "Now",
      save: "Save 50%",
      offer_ends_in: "Offer ends in",
      hurry: "Hurry!",
      limited: "Limited spaces available",
      last_2h: "Last 2 hours",
      last_1h: "Last hour",
      spaces_left: "spaces left",
      space_left: "space left",
      hrs: "Hrs", mins: "Mins", secs: "Secs",
    },
    hero: {
      eyebrow: "Custom CRM & Business Systems",
      line1: "Your business.",
      line2: "One system.",
      sub: "Custom CRM, business systems and digital tools built around the way your company actually works.",
      secondary: "Manage your leads, customers, team and operations — all in one place.",
      cta_primary: "Build my system",
      cta_secondary: "Explore services",
      fast: "Built around you", convert: "One central system",
      mobile: "Any device", seo: "Secure & scalable",
    },
    ethos: {
      eyebrow: "Our approach",
      title1: "Your business shouldn't have to fit the software.",
      title2: "The software should fit your business.",
      body: "Every business works differently. That's why OrangeBySite builds custom CRM and business management systems around your actual workflow — rather than forcing your company into generic off-the-shelf software. From managing leads and customers to bookings, staff, documents, follow-ups and reporting, we bring the moving parts of your business together into one system.",
    },
    services: {
      eyebrow: "Three core services",
      title1: "One partner.",
      title2: "Every layer of your digital business.",
      footnote: "Final pricing depends on scope and requirements. Every proposal is tailored.",
      // CRM (flagship)
      crm_index: "01",
      crm_heading1: "Your business.",
      crm_heading2: "One system.",
      crm_sub: "Custom CRM & Business Systems",
      crm_desc: "A system designed around how your business actually operates. Manage leads, customers, sales pipeline, staff, bookings, tasks, documents, follow-ups and reporting from one central platform.",
      crm_price_label: "Investment",
      crm_price: 5000,
      crm_price_prefix: "From",
      crm_tailored: "POA",
      crm_tailored_sub: "Priced around your requirements",
      web_price_disclaimer: "Starting price based on a standard 1–3 page custom website. Final pricing depends on the design, functionality and requirements of your project.",
      crm_care_label: "OrangeCare",
      // Numeric OrangeCare price now comes from GET /api/orangecare/catalog.
      // Keep the label + suffix/prefix strings only.
      crm_care: null,
      crm_care_suffix: "/month",
      crm_care_prefix: "From",
      crm_note: "Every system is different. We'll learn how your business works, understand what you need and provide a tailored proposal.",
      crm_cta: "Request a free discovery",
      crm_f: [
        "Lead & customer management",
        "Sales pipelines",
        "Staff & user accounts",
        "Tasks & follow-ups",
        "Booking workflows",
        "Automated emails",
        "Documents & quotations",
        "Dashboards & reporting",
        "Client portals",
        "Custom business workflows",
        "Integrations",
      ],
      // Website
      web_index: "02",
      web_heading: "More than a website.",
      web_sub: "Custom Business Websites",
      web_desc: "Premium websites designed around your brand, customers and commercial goals. From company sites to more advanced digital experiences, every build is designed specifically for your business.",
      web_price_label: "From",
      web_price: 1050,
      web_care_label: "OrangeCare",
      // Numeric prices now come from /api/orangecare/catalog (GBP source of truth).
      web_care_month: null,
      web_care_year: null,
      web_cta: "Get a quote",
      web_f: [
        "Bespoke design",
        "Multiple pages",
        "Mobile-first responsive design",
        "Enquiry capture",
        "SEO foundations",
        "Analytics",
        "Custom functionality",
        "Priority support options",
      ],
      // Landing
      lp_index: "03",
      lp_heading: "Get online. Fast.",
      lp_sub: "High-converting landing page",
      lp_desc: "A professionally designed, high-converting one-page website — ideal for new businesses, campaigns, products and services.",
      lp_normal: 1395,
      lp_offer: 700,
      lp_save: 700,
      lp_save_pct: "50% off",
      lp_launch: "Launch offer",
      lp_care_label: "OrangeCare",
      lp_care_month: null,
      lp_care_year: null,
      lp_care_desc: "Hosting, maintenance & support",
      lp_cta: "Claim the launch offer",
      lp_f: [
        "Premium 1-page website",
        "Mobile-first responsive design",
        "Custom layout",
        "Enquiry form",
        "Basic SEO setup",
        "Hosting",
        "Maintenance",
        "Support",
      ],
      badge_flagship: "Flagship",
      badge_offer: "Launch offer",
      save_word: "Save",
      per_month: "/month",
      per_year: "/year",
      or: "or",
    },
    care: {
      eyebrow: "OrangeCare",
      title1: "We build it.",
      title2: "We keep it running.",
      sub: "Your digital system shouldn't become another thing you have to manage. OrangeCare keeps your OrangeBySite website or business system hosted, maintained and supported.",
      tiers: [
        { key: "lp", name: "Landing Page" },
        { key: "web", name: "Custom Website" },
        { key: "crm", name: "CRM & Business Systems", from: true },
      ],
      note: "For CRM systems, pricing depends on system size, users, infrastructure, integrations and support requirements. Third-party costs (SMS, WhatsApp, AI/API usage, specialist integrations or high storage) may be charged separately where applicable.",
    },
    example: {
      eyebrow: "Custom CRM — example view",
      title1: "One system.",
      title2: "Everything connected.",
      sub: "Imagine your business without scattered spreadsheets, forgotten WhatsApp messages and disconnected systems. OrangeBySite brings your operation together.",
      cta: "Show us how you work",
      stats: [
        { label: "New leads", value: "42" },
        { label: "Follow-ups", value: "18" },
        { label: "Pipeline value", value: "__PIPELINE__" },
        { label: "Tasks today", value: "12" },
      ],
      modules: ["Dashboard", "Leads", "Customers", "Pipeline", "Bookings", "Tasks", "Team", "Documents", "Automations", "Reporting"],
      illustrative: "Illustrative only. Not a real client dataset.",
    },
    industries: {
      eyebrow: "Industries",
      title1: "Built around",
      title2: "your industry.",
      sub: "OrangeBySite systems can be customised for any business model. A few examples of what's possible:",
      list: [
        { name: "Real Estate", items: "Leads · Properties · Viewings · Agents · Commissions" },
        { name: "Clinics", items: "Enquiries · Appointments · Customers · Follow-ups" },
        { name: "Car Rental", items: "Fleet · Customers · Bookings · Availability" },
        { name: "Recruitment", items: "Candidates · Vacancies · Placements · Pipeline" },
        { name: "Service Businesses", items: "Customers · Jobs · Staff · Scheduling · Quotes" },
        { name: "Agencies", items: "Leads · Clients · Projects · Tasks · Billing" },
      ],
    },
    process: {
      eyebrow: "Our process",
      title1: "From how you work",
      title2: "to a system that fits.",
      steps: [
        { n: "01", title: "Tell us how you work", body: "We learn about your business, your current process and what's slowing you down." },
        { n: "02", title: "We design your system", body: "We map the workflow and design a system around your requirements." },
        { n: "03", title: "We build it", body: "Your custom platform is developed, branded and configured." },
        { n: "04", title: "You run your business", body: "Your team gets one central system designed around the way you actually operate." },
      ],
    },
    discovery: {
      eyebrow: "CRM discovery",
      title1: "Let's build",
      title2: "one system.",
      sub: "Tell us how your business currently works and what you'd like to improve. No obligation, no pushy sales.",
      f_name: "Full name *",
      f_business: "Business / company name *",
      f_email: "Email address *",
      f_phone: "Phone / WhatsApp *",
      f_industry: "Industry *",
      f_size: "Company size / number of users *",
      f_current: "Current website",
      f_stack: "How do you currently manage your business?",
      f_needs: "What would you like your website to manage?",
      needs_options: ["Leads","Customers","Sales pipeline","Bookings","Staff","Tasks","Documents","Quotations","Invoices","Follow-ups","Email automation","WhatsApp","Client portal","Reporting","Other"],
      f_problem: "What's the biggest problem you want to solve?",
      submit: "Request my free CRM discovery",
      sending: "Sending…",
      success_title: "Thank you.",
      success_sub: "We'll review your requirements and contact you to discuss how your OrangeBySite system could work.",
      success_again: "Send another enquiry",
      err_required: "Please fill in your name, email and contact number.",
      err_check: "Please check the highlighted fields.",
      err_field_required: "Please fill in this field.",
      err_email_invalid: "Please enter a valid email address.",
      err_phone_invalid: "Please enter a valid phone number.",
      err_fail: "Something went wrong. Please try again.",
      agree: "By submitting you agree to be contacted about your enquiry.",
      ph_name: "Jane Appleseed",
      ph_email: "jane@company.com",
      ph_phone: "+44 7700 000000",
      ph_business: "Your company",
      ph_industry: "e.g. Real estate, clinic, agency",
      ph_size: "e.g. 5 users, 20 users",
      ph_current: "https://",
      ph_stack: "e.g. Spreadsheets, WhatsApp, off-the-shelf CRM…",
      ph_problem: "e.g. Leads getting lost, follow-ups being missed, too much manual admin, no visibility across the business…",
    },
    work: {
      eyebrow: "Selected work",
      title: "Selected work.",
      sub: "A selection of brands, platforms and digital experiences we've designed and developed.",
    },
    final: {
      line1: "Ready to bring your business",
      line2: "into one system?",
      sub: "Start with a free CRM discovery — no obligation.",
      cta: "Build my system",
    },
    footer: {
      navigate: "Navigate", contact: "Contact",
      premium: "Custom CRM, business systems and digital experiences built around your business.",
      rights: "All rights reserved.",
      tagline: "Your business. One system.",
    },
  },
  de: {
    nav: { home: "Home", crm: "CRM", websites: "Websites", websites_custom: "Individuelle Websites", websites_landing: "Landing Pages", landing: "Landing Pages", work: "Referenzen", care: "OrangeCare", contact: "Kontakt", cta: "System bauen" },
    promo: {
      launch_title: "Launch-Angebot:",
      launch_desc_prefix: "Professionelle Landing Page für nur",
      launch_sub: "Perfekt für Produkt-Launches, Kampagnen und neue Angebote. Nur für kurze Zeit.",
      was: "Statt", now: "Jetzt", save: "50% sparen",
      offer_ends_in: "Angebot endet in", hurry: "Beeilung!", limited: "Begrenzte Plätze verfügbar",
      last_2h: "Letzte 2 Stunden", last_1h: "Letzte Stunde",
      spaces_left: "Plätze übrig", space_left: "Platz übrig",
      hrs: "Std", mins: "Min", secs: "Sek",
    },
    hero: {
      eyebrow: "Individuelles CRM & Business-Systeme",
      line1: "Ihr Unternehmen.", line2: "Ein System.",
      sub: "Individuelle CRM-, Business- und Digitalsysteme, gebaut um Ihre tatsächliche Arbeitsweise.",
      secondary: "Verwalten Sie Leads, Kunden, Team und Abläufe – alles an einem Ort.",
      cta_primary: "System bauen", cta_secondary: "Leistungen ansehen",
      fast: "Auf Sie zugeschnitten", convert: "Ein zentrales System",
      mobile: "Jedes Gerät", seo: "Sicher & skalierbar",
    },
    ethos: {
      eyebrow: "Unser Ansatz",
      title1: "Ihr Unternehmen sollte sich nicht der Software anpassen.",
      title2: "Die Software soll sich Ihrem Unternehmen anpassen.",
      body: "Jedes Unternehmen arbeitet anders. Darum baut OrangeBySite individuelle CRM- und Managementsysteme rund um Ihren tatsächlichen Workflow – statt Sie in generische Standardsoftware zu zwängen. Von Leads und Kunden bis zu Buchungen, Team, Dokumenten, Follow-ups und Reporting: Wir vereinen die beweglichen Teile Ihres Betriebs in einem System.",
    },
    services: {
      eyebrow: "Drei Kernleistungen",
      title1: "Ein Partner.", title2: "Jede Ebene Ihres digitalen Geschäfts.",
      footnote: "Die endgültige Preisgestaltung hängt von Umfang und Anforderungen ab. Jedes Angebot ist maßgeschneidert.",
      crm_index: "01", crm_heading1: "Ihr Unternehmen.", crm_heading2: "Ein System.",
      crm_sub: "Individuelles CRM & Business-Systeme",
      crm_desc: "Ein System, gebaut um Ihre tatsächliche Arbeitsweise. Leads, Kunden, Vertriebspipeline, Team, Buchungen, Aufgaben, Dokumente, Follow-ups und Reporting – zentral an einem Ort.",
      crm_price_label: "Individueller Aufbau", crm_price: 5000, crm_price_prefix: "Ab",
      crm_tailored: "POA",
      crm_tailored_sub: "Preis richtet sich nach Ihren Anforderungen",
      web_price_disclaimer: "Startpreis für eine Standard-Website mit ca. 1–3 Seiten. Der endgültige Preis hängt von Design, Funktionsumfang und Anforderungen ab.",
      crm_care_label: "OrangeCare", crm_care: null, crm_care_suffix: "/Monat", crm_care_prefix: "Ab",
      crm_note: "Jedes System ist anders. Wir verstehen Ihr Geschäft und liefern ein passendes Angebot.",
      crm_cta: "Kostenloses Discovery anfragen",
      crm_f: ["Lead- & Kundenverwaltung","Vertriebspipelines","Team- & Nutzerkonten","Aufgaben & Follow-ups","Buchungsabläufe","Automatische E-Mails","Dokumente & Angebote","Dashboards & Reporting","Kundenportale","Individuelle Workflows","Integrationen"],
      web_index: "02", web_heading: "Mehr als eine Website.",
      web_sub: "Individuelle Unternehmenswebsites",
      web_desc: "Premium-Websites rund um Ihre Marke, Kunden und geschäftlichen Ziele. Von Firmenwebsites bis hin zu komplexeren digitalen Erlebnissen — jedes Projekt speziell für Ihr Unternehmen.",
      web_price_label: "Ab", web_price: 1050,
      web_care_label: "OrangeCare", web_care_month: null, web_care_year: null,
      web_cta: "Angebot anfordern",
      web_f: ["Individuelles Design","Mehrere Seiten","Mobile-First responsiv","Anfrageerfassung","SEO-Grundlagen","Analytics","Individuelle Funktionen","Priorisierter Support"],
      lp_index: "03", lp_heading: "Schnell online.",
      lp_sub: "Hochkonvertierende Landing Page",
      lp_desc: "Professionell gestaltete, hochkonvertierende Einseiten-Website — ideal für neue Unternehmen, Kampagnen, Produkte und Dienstleistungen.",
      lp_normal: 1395, lp_offer: 700, lp_save: 700, lp_save_pct: "50% Rabatt",
      lp_launch: "Launch-Angebot",
      lp_care_label: "OrangeCare", lp_care_month: null, lp_care_year: null,
      lp_care_desc: "Hosting, Wartung & Support",
      lp_cta: "Launch-Angebot sichern",
      lp_f: ["Premium 1-Seiten-Website","Mobile-First responsiv","Individuelles Layout","Kontaktformular","Basis-SEO","Hosting","Wartung","Support"],
      badge_flagship: "Flaggschiff", badge_offer: "Launch-Angebot",
      save_word: "Sparen", per_month: "/Monat", per_year: "/Jahr", or: "oder",
    },
    care: {
      eyebrow: "OrangeCare", title1: "Wir bauen es.", title2: "Wir halten es am Laufen.",
      sub: "Ihr digitales System soll keine weitere Aufgabe sein. OrangeCare hält Ihre OrangeBySite-Website oder Ihr System gehostet, gewartet und unterstützt.",
      tiers: [
        { key: "lp", name: "Landing Page" },
        { key: "web", name: "Unternehmenswebsite" },
        { key: "crm", name: "CRM & Business-Systeme", from: true },
      ],
      note: "Bei CRM-Systemen hängt der Preis von Größe, Nutzern, Infrastruktur, Integrationen und Support ab. Drittkosten (SMS, WhatsApp, KI/API, Spezialintegrationen oder hoher Speicher) können separat berechnet werden.",
    },
    example: {
      eyebrow: "Individuelles CRM — Beispielansicht",
      title1: "Ein System.", title2: "Alles verbunden.",
      sub: "Stellen Sie sich Ihr Unternehmen ohne verstreute Tabellen, vergessene WhatsApp-Nachrichten und getrennte Systeme vor. OrangeBySite bringt alles zusammen.",
      cta: "Zeigen Sie uns Ihre Arbeitsweise",
      stats: [
        { label: "Neue Leads", value: "42" },
        { label: "Follow-ups", value: "18" },
        { label: "Pipeline-Wert", value: "__PIPELINE__" },
        { label: "Aufgaben heute", value: "12" },
      ],
      modules: ["Dashboard","Leads","Kunden","Pipeline","Buchungen","Aufgaben","Team","Dokumente","Automationen","Reporting"],
      illustrative: "Nur zur Veranschaulichung. Keine echten Kundendaten.",
    },
    industries: {
      eyebrow: "Branchen",
      title1: "Gebaut für", title2: "Ihre Branche.",
      sub: "OrangeBySite-Systeme lassen sich für jedes Geschäftsmodell anpassen. Ein paar Beispiele:",
      list: [
        { name: "Immobilien", items: "Leads · Objekte · Besichtigungen · Makler · Provisionen" },
        { name: "Kliniken", items: "Anfragen · Termine · Kunden · Follow-ups" },
        { name: "Autovermietung", items: "Flotte · Kunden · Buchungen · Verfügbarkeit" },
        { name: "Recruiting", items: "Kandidaten · Stellen · Placements · Pipeline" },
        { name: "Dienstleistungen", items: "Kunden · Aufträge · Team · Planung · Angebote" },
        { name: "Agenturen", items: "Leads · Kunden · Projekte · Aufgaben · Abrechnung" },
      ],
    },
    process: {
      eyebrow: "Unser Prozess",
      title1: "Von Ihrer Arbeitsweise", title2: "zu einem passenden System.",
      steps: [
        { n: "01", title: "Erzählen Sie uns wie Sie arbeiten", body: "Wir lernen Ihr Unternehmen kennen und verstehen, was Sie ausbremst." },
        { n: "02", title: "Wir gestalten Ihr System", body: "Wir kartieren den Workflow und entwerfen ein System nach Ihren Anforderungen." },
        { n: "03", title: "Wir bauen es", body: "Ihre individuelle Plattform wird entwickelt, gebrandet und konfiguriert." },
        { n: "04", title: "Sie führen Ihr Unternehmen", body: "Ihr Team erhält ein zentrales System, gebaut um Ihre Arbeitsweise." },
      ],
    },
    discovery: {
      eyebrow: "CRM-Discovery",
      title1: "Lassen Sie uns", title2: "ein System bauen.",
      sub: "Erzählen Sie uns, wie Ihr Unternehmen aktuell arbeitet und was Sie verbessern möchten. Unverbindlich, kein Verkaufsdruck.",
      f_name: "Vollständiger Name *", f_business: "Unternehmen *",
      f_email: "E-Mail *", f_phone: "Telefon / WhatsApp *",
      f_industry: "Branche *", f_size: "Unternehmensgröße / Nutzer *",
      f_current: "Aktuelle Website",
      f_stack: "Wie verwalten Sie Ihr Unternehmen aktuell?",
      f_needs: "Was soll Ihr System verwalten?",
      needs_options: ["Leads","Kunden","Vertriebspipeline","Buchungen","Team","Aufgaben","Dokumente","Angebote","Rechnungen","Follow-ups","E-Mail-Automation","WhatsApp","Kundenportal","Reporting","Sonstiges"],
      f_problem: "Was ist das größte Problem, das Sie lösen möchten?",
      submit: "Kostenlose CRM-Discovery anfragen", sending: "Sende…",
      success_title: "Danke.", success_sub: "Wir prüfen Ihre Anforderungen und melden uns, um zu besprechen, wie Ihr OrangeBySite-System aussehen könnte.",
      success_again: "Weitere Anfrage senden",
      err_required: "Bitte Name, E-Mail und Telefon ausfüllen.",
      err_check: "Bitte überprüfen Sie die markierten Felder.",
      err_field_required: "Bitte dieses Feld ausfüllen.",
      err_email_invalid: "Bitte eine gültige E-Mail-Adresse eingeben.",
      err_fail: "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
      agree: "Mit dem Absenden stimmen Sie zu, zu Ihrer Anfrage kontaktiert zu werden.",
      ph_name: "Max Mustermann", ph_email: "max@firma.de", ph_phone: "+49 …",
      ph_business: "Ihr Unternehmen", ph_industry: "z. B. Immobilien, Klinik, Agentur",
      ph_size: "z. B. 5 Nutzer, 20 Nutzer", ph_current: "https://",
      ph_stack: "Tabellen, WhatsApp, Standard-CRM…",
      ph_problem: "z. B. Leads gehen verloren, keine Sicht auf Pipeline, Follow-ups verschwinden…",
    },
    work: {
      eyebrow: "Ausgewählte Arbeiten", title: "Ausgewählte Arbeiten.",
      sub: "Eine Auswahl an Marken, Plattformen und digitalen Erlebnissen, die wir gestaltet und entwickelt haben.",
    },
    final: {
      line1: "Bereit, Ihr Unternehmen", line2: "in ein System zu bringen?",
      sub: "Starten Sie mit einer kostenlosen CRM-Discovery — unverbindlich.",
      cta: "System bauen",
    },
    footer: {
      navigate: "Navigation", contact: "Kontakt",
      premium: "Individuelles CRM, Business-Systeme und digitale Erlebnisse, gebaut um Ihr Unternehmen.",
      rights: "Alle Rechte vorbehalten.", tagline: "Ihr Unternehmen. Ein System.",
    },
  },
  es: {
    nav: { home: "Inicio", crm: "CRM", websites: "Webs", websites_custom: "Webs a medida", websites_landing: "Landing Pages", landing: "Landing Pages", work: "Casos", care: "OrangeCare", contact: "Contacto", cta: "Construir mi sistema" },
    promo: {
      launch_title: "Oferta de lanzamiento:",
      launch_desc_prefix: "Landing page profesional por solo",
      launch_sub: "Perfecta para lanzamientos, campañas y nuevas ofertas. Por tiempo limitado.",
      was: "Antes", now: "Ahora", save: "Ahorra 50%",
      offer_ends_in: "La oferta acaba en", hurry: "¡Rápido!", limited: "Plazas limitadas",
      last_2h: "Últimas 2 horas", last_1h: "Última hora",
      spaces_left: "plazas libres", space_left: "plaza libre",
      hrs: "Hrs", mins: "Min", secs: "Seg",
    },
    hero: {
      eyebrow: "CRM y sistemas de negocio a medida",
      line1: "Tu negocio.", line2: "Un sistema.",
      sub: "CRM, sistemas de negocio y herramientas digitales construidos alrededor de cómo funciona realmente tu empresa.",
      secondary: "Gestiona leads, clientes, equipo y operaciones — todo en un solo lugar.",
      cta_primary: "Construir mi sistema", cta_secondary: "Explorar servicios",
      fast: "Hecho a tu medida", convert: "Un sistema central",
      mobile: "Cualquier dispositivo", seo: "Seguro y escalable",
    },
    ethos: {
      eyebrow: "Nuestro enfoque",
      title1: "Tu negocio no debería adaptarse al software.",
      title2: "El software debería adaptarse a tu negocio.",
      body: "Cada negocio funciona de manera diferente. Por eso OrangeBySite construye sistemas CRM y de gestión a medida alrededor de tu flujo real — en lugar de forzar a tu empresa a usar software genérico. Desde leads y clientes hasta reservas, equipo, documentos, seguimientos y reportes, reunimos las piezas de tu operación en un solo sistema.",
    },
    services: {
      eyebrow: "Tres servicios principales",
      title1: "Un socio.", title2: "Cada capa de tu negocio digital.",
      footnote: "El precio final depende del alcance y los requisitos. Cada propuesta es a medida.",
      crm_index: "01", crm_heading1: "Tu negocio.", crm_heading2: "Un sistema.",
      crm_sub: "CRM y sistemas de negocio a medida",
      crm_desc: "Un sistema diseñado según cómo funciona realmente tu negocio. Gestiona leads, clientes, pipeline, equipo, reservas, tareas, documentos, seguimientos y reportes desde una sola plataforma.",
      crm_price_label: "Desarrollo a medida", crm_price: 5000, crm_price_prefix: "Desde",
      crm_tailored: "POA",
      crm_tailored_sub: "Precio según tus requisitos",
      web_price_disclaimer: "Precio inicial para una web estándar de 1 a 3 páginas. El precio final depende del diseño, la funcionalidad y los requisitos del proyecto.",
      crm_care_label: "OrangeCare", crm_care: null, crm_care_suffix: "/mes", crm_care_prefix: "Desde",
      crm_note: "Cada sistema es distinto. Estudiaremos tu negocio y prepararemos una propuesta a medida.",
      crm_cta: "Solicitar discovery gratuito",
      crm_f: ["Gestión de leads y clientes","Pipelines de ventas","Cuentas de equipo","Tareas y seguimientos","Flujos de reservas","Emails automáticos","Documentos y presupuestos","Dashboards e informes","Portales de cliente","Flujos de trabajo a medida","Integraciones"],
      web_index: "02", web_heading: "Más que una web.",
      web_sub: "Webs corporativas a medida",
      web_desc: "Webs premium diseñadas alrededor de tu marca, clientes y objetivos comerciales. Desde webs corporativas hasta experiencias digitales avanzadas.",
      web_price_label: "Desde", web_price: 1050,
      web_care_label: "OrangeCare", web_care_month: null, web_care_year: null,
      web_cta: "Solicitar presupuesto",
      web_f: ["Diseño a medida","Múltiples páginas","Responsive mobile-first","Captura de leads","SEO base","Analytics","Funcionalidad a medida","Opciones de soporte prioritario"],
      lp_index: "03", lp_heading: "Online. Rápido.",
      lp_sub: "Landing de alta conversión",
      lp_desc: "Una landing de una página diseñada profesionalmente para convertir — ideal para nuevos negocios, campañas, productos y servicios.",
      lp_normal: 1395, lp_offer: 700, lp_save: 700, lp_save_pct: "50% de descuento",
      lp_launch: "Oferta de lanzamiento",
      lp_care_label: "OrangeCare", lp_care_month: null, lp_care_year: null,
      lp_care_desc: "Hosting, mantenimiento y soporte",
      lp_cta: "Conseguir la oferta",
      lp_f: ["Landing premium 1 página","Responsive mobile-first","Layout a medida","Formulario","SEO básico","Hosting","Mantenimiento","Soporte"],
      badge_flagship: "Producto estrella", badge_offer: "Oferta de lanzamiento",
      save_word: "Ahorra", per_month: "/mes", per_year: "/año", or: "o",
    },
    care: {
      eyebrow: "OrangeCare", title1: "Lo construimos.", title2: "Lo mantenemos funcionando.",
      sub: "Tu sistema digital no debería convertirse en otra cosa que gestionar. OrangeCare aloja, mantiene y da soporte a tu web o sistema OrangeBySite.",
      tiers: [
        { key: "lp", name: "Landing Page" },
        { key: "web", name: "Web corporativa" },
        { key: "crm", name: "CRM y sistemas de negocio", from: true },
      ],
      note: "Para sistemas CRM, el precio depende de tamaño, usuarios, infraestructura, integraciones y soporte. Costes de terceros (SMS, WhatsApp, IA/API, integraciones especiales o almacenamiento alto) pueden facturarse aparte.",
    },
    example: {
      eyebrow: "CRM a medida — vista de ejemplo",
      title1: "Un sistema.", title2: "Todo conectado.",
      sub: "Imagina tu negocio sin hojas de cálculo dispersas, WhatsApps olvidados y sistemas desconectados. OrangeBySite unifica tu operación.",
      cta: "Cuéntanos cómo trabajas",
      stats: [
        { label: "Nuevos leads", value: "42" },
        { label: "Seguimientos", value: "18" },
        { label: "Valor del pipeline", value: "__PIPELINE__" },
        { label: "Tareas hoy", value: "12" },
      ],
      modules: ["Dashboard","Leads","Clientes","Pipeline","Reservas","Tareas","Equipo","Documentos","Automatizaciones","Informes"],
      illustrative: "Solo ilustrativo. No son datos reales de cliente.",
    },
    industries: {
      eyebrow: "Sectores",
      title1: "Diseñado para", title2: "tu sector.",
      sub: "Los sistemas de OrangeBySite se pueden personalizar para cualquier modelo de negocio. Algunos ejemplos:",
      list: [
        { name: "Inmobiliaria", items: "Leads · Propiedades · Visitas · Agentes · Comisiones" },
        { name: "Clínicas", items: "Consultas · Citas · Clientes · Seguimientos" },
        { name: "Alquiler de coches", items: "Flota · Clientes · Reservas · Disponibilidad" },
        { name: "Selección", items: "Candidatos · Vacantes · Colocaciones · Pipeline" },
        { name: "Servicios", items: "Clientes · Trabajos · Personal · Agenda · Presupuestos" },
        { name: "Agencias", items: "Leads · Clientes · Proyectos · Tareas · Facturación" },
      ],
    },
    process: {
      eyebrow: "Nuestro proceso",
      title1: "De cómo trabajas", title2: "a un sistema a tu medida.",
      steps: [
        { n: "01", title: "Cuéntanos cómo trabajas", body: "Conocemos tu negocio, tu proceso actual y qué te ralentiza." },
        { n: "02", title: "Diseñamos tu sistema", body: "Mapeamos el flujo y diseñamos el sistema según tus requisitos." },
        { n: "03", title: "Lo construimos", body: "Desarrollamos, personalizamos y configuramos tu plataforma." },
        { n: "04", title: "Diriges tu negocio", body: "Tu equipo recibe un sistema central diseñado para vuestra forma de trabajar." },
      ],
    },
    discovery: {
      eyebrow: "Discovery CRM",
      title1: "Construyamos", title2: "un sistema.",
      sub: "Cuéntanos cómo trabaja tu negocio ahora y qué te gustaría mejorar. Sin compromiso, sin ventas agresivas.",
      f_name: "Nombre completo *", f_business: "Empresa *",
      f_email: "Email *", f_phone: "Teléfono / WhatsApp *",
      f_industry: "Sector *", f_size: "Tamaño / número de usuarios *",
      f_current: "Web actual",
      f_stack: "¿Cómo gestionas actualmente tu negocio?",
      f_needs: "¿Qué quieres que gestione tu sistema?",
      needs_options: ["Leads","Clientes","Pipeline","Reservas","Personal","Tareas","Documentos","Presupuestos","Facturas","Seguimientos","Automatización de emails","WhatsApp","Portal de cliente","Informes","Otro"],
      f_problem: "¿Cuál es el mayor problema que quieres resolver?",
      submit: "Solicitar mi Discovery CRM gratis", sending: "Enviando…",
      success_title: "Gracias.", success_sub: "Revisaremos tus requisitos y te contactaremos para ver cómo podría funcionar tu sistema OrangeBySite.",
      success_again: "Enviar otra solicitud",
      err_required: "Por favor completa nombre, email y teléfono.",
      err_check: "Por favor revisa los campos marcados.",
      err_field_required: "Por favor completa este campo.",
      err_email_invalid: "Por favor introduce un email válido.",
      err_fail: "Algo salió mal. Inténtalo de nuevo.",
      agree: "Al enviar aceptas ser contactado sobre tu solicitud.",
      ph_name: "Ana García", ph_email: "ana@empresa.com", ph_phone: "+34 …",
      ph_business: "Tu empresa", ph_industry: "p. ej. inmobiliaria, clínica, agencia",
      ph_size: "p. ej. 5 usuarios, 20 usuarios", ph_current: "https://",
      ph_stack: "Excel, WhatsApp, CRM genérico…",
      ph_problem: "p. ej. se pierden leads, no hay visibilidad del pipeline, los seguimientos se olvidan…",
    },
    work: {
      eyebrow: "Trabajos destacados", title: "Trabajos destacados.",
      sub: "Una selección de marcas, plataformas y experiencias digitales que hemos diseñado y desarrollado.",
    },
    final: {
      line1: "¿Listo para unificar tu negocio", line2: "en un solo sistema?",
      sub: "Empieza con un Discovery CRM gratuito — sin compromiso.",
      cta: "Construir mi sistema",
    },
    footer: {
      navigate: "Navegar", contact: "Contacto",
      premium: "CRM, sistemas de negocio y experiencias digitales a medida construidos alrededor de tu empresa.",
      rights: "Todos los derechos reservados.", tagline: "Tu negocio. Un sistema.",
    },
  },
  ar: {
    nav: { home: "الرئيسية", crm: "CRM", websites: "المواقع", websites_custom: "مواقع مخصصة", websites_landing: "صفحات الهبوط", landing: "صفحات الهبوط", work: "أعمالنا", care: "أورنج كير", contact: "تواصل معنا", cta: "ابنِ نظامي" },
    promo: {
      launch_title: "عرض الإطلاق:",
      launch_desc_prefix: "صفحة هبوط احترافية بـ",
      launch_sub: "مثالية لإطلاق المنتجات والحملات والعروض الجديدة. لفترة محدودة.",
      was: "بدلاً من", now: "الآن", save: "وفر 50%",
      offer_ends_in: "ينتهي العرض خلال", hurry: "أسرع!", limited: "الأماكن محدودة",
      last_2h: "آخر ساعتين", last_1h: "الساعة الأخيرة",
      spaces_left: "أماكن متبقية", space_left: "مكان متبقي",
      hrs: "ساعات", mins: "دقائق", secs: "ثوانٍ",
    },
    hero: {
      eyebrow: "أنظمة CRM وحلول الأعمال المخصصة",
      line1: "أعمالك.", line2: "نظام واحد.",
      sub: "أنظمة CRM وحلول أعمال وأدوات رقمية مصممة حول طريقة عمل شركتك فعلياً.",
      secondary: "أدر العملاء المحتملين والعملاء والفريق والعمليات — كل ذلك في مكان واحد.",
      cta_primary: "ابنِ نظامي", cta_secondary: "استكشف الخدمات",
      fast: "مصمم لك", convert: "نظام مركزي واحد",
      mobile: "أي جهاز", seo: "آمن وقابل للتوسع",
    },
    ethos: {
      eyebrow: "منهجيتنا",
      title1: "شركتك ليست هي التي تتكيف مع البرنامج.",
      title2: "بل البرنامج هو من يتكيف مع شركتك.",
      body: "كل شركة تعمل بطريقة مختلفة. لذلك تبني OrangeBySite أنظمة CRM وإدارة أعمال مخصصة حول تدفق عملك الحقيقي — بدلاً من إجبار شركتك على استخدام برامج جاهزة عامة. من إدارة العملاء المحتملين والعملاء إلى الحجوزات والفرق والوثائق والمتابعات والتقارير، نجمع أجزاء عملياتك في نظام واحد.",
    },
    services: {
      eyebrow: "ثلاث خدمات رئيسية",
      title1: "شريك واحد.", title2: "كل طبقة من أعمالك الرقمية.",
      footnote: "السعر النهائي يعتمد على النطاق والمتطلبات. كل عرض مصمم خصيصاً.",
      crm_index: "01", crm_heading1: "أعمالك.", crm_heading2: "نظام واحد.",
      crm_sub: "أنظمة CRM وحلول أعمال مخصصة",
      crm_desc: "نظام مصمم حول طريقة عمل شركتك فعلياً. أدر العملاء المحتملين والعملاء ومسار المبيعات والفريق والحجوزات والمهام والوثائق والمتابعات والتقارير من منصة مركزية واحدة.",
      crm_price_label: "بناء مخصص", crm_price: 5000, crm_price_prefix: "ابتداءً من",
      crm_tailored: "POA",
      crm_tailored_sub: "التسعير حسب متطلباتك",
      web_price_disclaimer: "السعر يبدأ من موقع مخصص من 1 إلى 3 صفحات. يعتمد السعر النهائي على التصميم والوظائف والمتطلبات.",
      crm_care_label: "أورنج كير", crm_care: null, crm_care_suffix: "/شهرياً", crm_care_prefix: "من",
      crm_note: "كل نظام مختلف. سنتعرف على طريقة عمل شركتك ونقدم عرضاً مخصصاً.",
      crm_cta: "اطلب استكشافاً مجانياً",
      crm_f: ["إدارة العملاء المحتملين والعملاء","مسارات المبيعات","حسابات الفريق والمستخدمين","المهام والمتابعات","تدفقات الحجز","رسائل بريد آلية","المستندات والعروض","لوحات معلومات وتقارير","بوابات العملاء","تدفقات عمل مخصصة","تكاملات"],
      web_index: "02", web_heading: "أكثر من موقع.",
      web_sub: "مواقع أعمال مخصصة",
      web_desc: "مواقع بريميوم مصممة حول علامتك التجارية وعملائك وأهدافك التجارية.",
      web_price_label: "ابتداءً من", web_price: 1050,
      web_care_label: "أورنج كير", web_care_month: null, web_care_year: null,
      web_cta: "اطلب عرض سعر",
      web_f: ["تصميم مخصص","صفحات متعددة","تصميم متجاوب","التقاط الاستفسارات","أساسيات SEO","تحليلات","وظائف مخصصة","خيارات دعم متقدم"],
      lp_index: "03", lp_heading: "أونلاين. بسرعة.",
      lp_sub: "صفحة هبوط عالية التحويل",
      lp_desc: "صفحة هبوط بتصميم احترافي عالي التحويل — مثالية للأعمال الجديدة والحملات والمنتجات والخدمات.",
      lp_normal: 1395, lp_offer: 700, lp_save: 700, lp_save_pct: "خصم 50%",
      lp_launch: "عرض الإطلاق",
      lp_care_label: "أورنج كير", lp_care_month: null, lp_care_year: null,
      lp_care_desc: "استضافة وصيانة ودعم",
      lp_cta: "احصل على العرض",
      lp_f: ["صفحة بريميوم واحدة","تصميم متجاوب","تخطيط مخصص","نموذج تواصل","إعداد SEO أساسي","استضافة","صيانة","دعم"],
      badge_flagship: "المميز", badge_offer: "عرض الإطلاق",
      save_word: "وفر", per_month: "/شهرياً", per_year: "/سنوياً", or: "أو",
    },
    care: {
      eyebrow: "أورنج كير", title1: "نبنيه.", title2: "ونبقيه يعمل.",
      sub: "نظامك الرقمي لا يجب أن يصبح مهمة أخرى تديرها. أورنج كير يستضيف موقع أو نظام OrangeBySite الخاص بك ويحافظ عليه ويدعمه.",
      tiers: [
        { key: "lp", name: "صفحة هبوط" },
        { key: "web", name: "موقع أعمال" },
        { key: "crm", name: "CRM وأنظمة أعمال", from: true },
      ],
      note: "لأنظمة CRM يعتمد السعر على حجم النظام والمستخدمين والبنية التحتية والتكاملات والدعم. تكاليف الطرف الثالث (SMS، واتساب، ذكاء اصطناعي/API، تكاملات خاصة، أو تخزين مرتفع) قد تُحتسب منفصلة.",
    },
    example: {
      eyebrow: "CRM مخصص — عرض توضيحي",
      title1: "نظام واحد.", title2: "كل شيء متصل.",
      sub: "تخيل أعمالك دون جداول مبعثرة ورسائل واتساب منسية وأنظمة مقطوعة الاتصال. OrangeBySite يجمع عملياتك.",
      cta: "أخبرنا كيف تعمل",
      stats: [
        { label: "عملاء محتملون جدد", value: "42" },
        { label: "متابعات", value: "18" },
        { label: "قيمة المسار", value: "__PIPELINE__" },
        { label: "مهام اليوم", value: "12" },
      ],
      modules: ["اللوحة","العملاء المحتملون","العملاء","المسار","الحجوزات","المهام","الفريق","المستندات","الأتمتة","التقارير"],
      illustrative: "توضيحي فقط. ليست بيانات عميل حقيقي.",
    },
    industries: {
      eyebrow: "القطاعات",
      title1: "مصمم حول", title2: "قطاعك.",
      sub: "أنظمة OrangeBySite يمكن تخصيصها لأي نموذج عمل. بعض الأمثلة:",
      list: [
        { name: "العقارات", items: "العملاء المحتملون · العقارات · المعاينات · الوكلاء · العمولات" },
        { name: "العيادات", items: "الاستفسارات · المواعيد · العملاء · المتابعات" },
        { name: "تأجير السيارات", items: "الأسطول · العملاء · الحجوزات · التوفر" },
        { name: "التوظيف", items: "المرشحون · الوظائف · التعيينات · المسار" },
        { name: "قطاع الخدمات", items: "العملاء · الوظائف · الفريق · الجدولة · العروض" },
        { name: "الوكالات", items: "العملاء المحتملون · العملاء · المشاريع · المهام · الفوترة" },
      ],
    },
    process: {
      eyebrow: "منهجيتنا",
      title1: "من طريقة عملك", title2: "إلى نظام يناسبك.",
      steps: [
        { n: "01", title: "أخبرنا كيف تعمل", body: "نتعرف على أعمالك وعملياتك الحالية وما الذي يبطئك." },
        { n: "02", title: "نصمم نظامك", body: "نرسم تدفق العمل ونصمم نظاماً يلبي متطلباتك." },
        { n: "03", title: "نبنيه", body: "يتم تطوير منصتك المخصصة وتصميمها وضبطها." },
        { n: "04", title: "أنت تدير أعمالك", body: "يحصل فريقك على نظام مركزي مصمم حول طريقة عملكم فعلياً." },
      ],
    },
    discovery: {
      eyebrow: "استكشاف CRM",
      title1: "لنبنِ", title2: "نظامك.",
      sub: "أخبرنا كيف تعمل شركتك حالياً وما الذي تود تحسينه. بدون التزام، بدون ضغط بيع.",
      f_name: "الاسم الكامل *", f_business: "اسم الشركة *",
      f_email: "البريد الإلكتروني *", f_phone: "الهاتف / واتساب *",
      f_industry: "القطاع *", f_size: "حجم الشركة / عدد المستخدمين *",
      f_current: "الموقع الحالي",
      f_stack: "كيف تدير أعمالك حالياً؟",
      f_needs: "ما الذي تود أن يديره نظامك؟",
      needs_options: ["العملاء المحتملون","العملاء","مسار المبيعات","الحجوزات","الفريق","المهام","المستندات","العروض","الفواتير","المتابعات","أتمتة البريد","واتساب","بوابة العملاء","التقارير","أخرى"],
      f_problem: "ما أكبر مشكلة تود حلها؟",
      submit: "اطلب استكشاف CRM مجاني", sending: "جارٍ الإرسال…",
      success_title: "شكراً لك.", success_sub: "سنراجع متطلباتك ونتواصل معك لمناقشة كيف يمكن أن يعمل نظام OrangeBySite الخاص بك.",
      success_again: "إرسال طلب آخر",
      err_required: "يرجى تعبئة الاسم والبريد ورقم الهاتف.",
      err_check: "يرجى مراجعة الحقول المميزة.",
      err_field_required: "يرجى تعبئة هذا الحقل.",
      err_email_invalid: "يرجى إدخال بريد إلكتروني صالح.",
      err_fail: "حدث خطأ. حاول مرة أخرى.",
      agree: "بإرسال هذا النموذج أنت توافق على أن نتواصل معك بشأن طلبك.",
      ph_name: "أحمد محمد", ph_email: "ahmed@company.com", ph_phone: "+971 …",
      ph_business: "شركتك", ph_industry: "مثل: عقارات، عيادة، وكالة",
      ph_size: "مثل: 5 مستخدمين، 20 مستخدماً", ph_current: "https://",
      ph_stack: "جداول، واتساب، CRM جاهز…",
      ph_problem: "مثال: العملاء المحتملون يضيعون، لا رؤية للمسار، المتابعات تُنسى…",
    },
    work: {
      eyebrow: "أعمال مختارة", title: "أعمال مختارة.",
      sub: "مجموعة مختارة من العلامات التجارية والمنصات والتجارب الرقمية التي صممناها وطورناها.",
    },
    final: {
      line1: "جاهز لتوحيد أعمالك", line2: "في نظام واحد؟",
      sub: "ابدأ باستكشاف CRM مجاني — بدون التزام.",
      cta: "ابنِ نظامي",
    },
    footer: {
      navigate: "التنقل", contact: "تواصل معنا",
      premium: "أنظمة CRM وحلول أعمال وتجارب رقمية مصممة حول شركتك.",
      rights: "جميع الحقوق محفوظة.", tagline: "أعمالك. نظام واحد.",
    },
  },
};

const LS_CURRENCY = "obs_currency";
const LS_LANG = "obs_lang";

export function AppProvider({ children }) {
  const [currency, setCurrencyState] = useState("GBP");
  const [language, setLanguageState] = useState("en");

  useEffect(() => {
    try {
      const c = localStorage.getItem(LS_CURRENCY);
      const l = localStorage.getItem(LS_LANG);
      if (c && CURRENCIES.some((x) => x.code === c)) setCurrencyState(c);
      if (l && LANGUAGES.some((x) => x.code === l)) setLanguageState(l);
    } catch {}
  }, []);

  const langInfo = useMemo(
    () => LANGUAGES.find((l) => l.code === language) || LANGUAGES[0],
    [language]
  );

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute("dir", langInfo.dir);
    html.setAttribute("lang", language);
  }, [langInfo, language]);

  const setCurrency = (c) => {
    setCurrencyState(c);
    try { localStorage.setItem(LS_CURRENCY, c); } catch {}
  };
  const setLanguage = (l) => {
    setLanguageState(l);
    try { localStorage.setItem(LS_LANG, l); } catch {}
  };

  // Convert `amount` from base currency into the currently selected currency and format.
  // `opts.base` = source currency of the raw number (default "AED" for legacy translations).
  // `opts.round` = round to nearest N (per-currency default if omitted).
  const formatPrice = (amount, opts = {}) => {
    const base = opts.base || "AED";
    const cur = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];
    const baseCur = CURRENCIES.find((c) => c.code === base) || CURRENCIES[0];
    // amount → AED → selected currency
    const aed = Number(amount) / baseCur.rate;
    let v = aed * cur.rate;
    const defaultRound = currency === "AED" ? 25 : 5;
    const round = opts.round ?? defaultRound;
    v = round > 0 ? Math.round(v / round) * round : Math.round(v);
    const formatted = v.toLocaleString("en-US");
    return `${cur.symbol}${formatted}`;
  };

  // Dot-path translation lookup with EN fallback.
  const t = (path) => {
    const segs = path.split(".");
    const walk = (obj) => segs.reduce((a, k) => (a && a[k] !== undefined ? a[k] : undefined), obj);
    return walk(DICT[language]) ?? walk(DICT.en) ?? path;
  };

  const value = {
    currency, setCurrency,
    language, setLanguage,
    formatPrice,
    CURRENCIES, LANGUAGES,
    dir: langInfo.dir,
    t,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
}
